import React from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Stack,
  Alert,
  Divider,
  Code
} from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconHome } from '@tabler/icons-react';
import { clearAuthState } from '../../utils/authUtils';
import { Link } from 'react-router-dom';

interface DashboardErrorFallbackProps {
  error?: Error | null;
  resetErrorBoundary?: () => void;
}

/**
 * Error fallback component specifically for dashboard loading issues
 */
export const DashboardErrorFallback: React.FC<DashboardErrorFallbackProps> = ({
  error,
  resetErrorBoundary
}) => {
  const handleRefresh = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  const handleResetAuth = async () => {
    await clearAuthState();
    window.location.href = '/login';
  };

  return (
    <Container size="md" py="xl">
      <Paper p="xl" shadow="md" radius="md" withBorder>
        <Stack spacing="lg">
          <Title order={2} align="center">Dashboard Loading Error</Title>
          
          <Alert 
            icon={<IconAlertTriangle size={20} />} 
            title="We encountered an issue loading your dashboard" 
            color="red"
          >
            This could be due to a temporary connection issue, authentication problem, or data loading error.
          </Alert>
          
          {error && (
            <Paper withBorder p="md" sx={{ backgroundColor: '#f9f9f9' }}>
              <Text size="sm" weight={500} mb="xs">Error details:</Text>
              <Code block>
                {error.message || 'Unknown error'}
              </Code>
            </Paper>
          )}
          
          <Divider />
          
          <Text size="sm" color="dimmed">
            You can try the following solutions:
          </Text>
          
          <Group position="center" spacing="md">
            <Button
              leftIcon={<IconRefresh size={18} />}
              onClick={handleRefresh}
              color="blue"
            >
              Refresh Page
            </Button>
            
            <Button
              variant="outline"
              onClick={handleResetAuth}
              color="red"
            >
              Reset Authentication
            </Button>
            
            <Button
              component={Link}
              to="/auth-help"
              variant="subtle"
              leftIcon={<IconHome size={18} />}
            >
              Go to Troubleshooter
            </Button>
          </Group>
          
          <Text size="xs" color="dimmed" align="center">
            If the problem persists, please contact support or your system administrator.
          </Text>
        </Stack>
      </Paper>
    </Container>
  );
}; 